import { useMemo } from "react";
import InputDropdownFilter from "../Inputs/InputDropdownFilter/InputDropdownFilter";
import { FilterField } from "../../../types/types";
import DateRangeCalendar from "../DateRangeCalendar/DateRangeCalendar";
import Tag from "../Tag/Tag";
import InputDropdown_New from "../../inputs/InputDropdown/InputDropdown_New";

type FiltersDrawerProps<T extends Record<string, any>> = {
  filterFields: FilterField<T>[];
  data: T[];
  filterValues: Record<string, any>;
  onChange: (key: string, value: any) => void;
  onClear: () => void;
};

function FiltersDrawer<T extends Record<string, any>>({
  filterFields,
  data,
  filterValues,
  onChange,
  onClear,
}: FiltersDrawerProps<T>) {
  const optionsMap = useMemo(() => {
    const map: Record<string, { label: string; value: any }[]> = {};

    filterFields.forEach(({ key, type, labelMap, objectProp }) => {
      if (type === "select" || type === "multiselect" || type === "cards") {
        const otherFilters = Object.entries(filterValues).filter(
          ([k, v]) =>
            k !== String(key) &&
            v !== undefined &&
            v !== "" &&
            !(Array.isArray(v) && v.length === 0)
        );

        const temp = data.filter((row) => {
          return otherFilters.every(([k, v]) => {
            const fieldDef = filterFields.find((f) => String(f.key) === k);
            if (!fieldDef) return true;
            const cell = row[k as keyof T];

            if (
              fieldDef.type === "multiselect" &&
              Array.isArray(v) &&
              fieldDef.objectProp
            ) {
              return v.map(x=>x.value).includes(cell?.[fieldDef.objectProp]);
            }

            if (fieldDef.type === "multiselect" && Array.isArray(v)) {
              return v.map(x=>x.value).includes(cell);
            }

            if (fieldDef.type === "select") {
              return cell === v;
            }
            if (fieldDef.type === "date" && v.start && v.end) {
              const d = new Date(String(cell));
              return d >= v.start && d <= v.end;
            }
            return String(cell).toLowerCase().includes(String(v).toLowerCase());
          });
        });

        const unique = Array.from(
          new Set(
            temp.map((r) => {
              const cell = r[key as keyof T];
              if (objectProp && cell && typeof cell === "object") {
                return (cell as any)[objectProp];
              }
              return cell;
            })
          )
        ).filter((val) => val !== "");

        map[String(key)] = unique.map((val) => {
          const label =
            labelMap && typeof val === "number" && labelMap[val]
              ? labelMap[val]
              : String(val);
          return { label, value: val };
        });
      }
    });

    return map;
  }, [filterFields, data, filterValues]);

  return (
    <div className="filters-drawer">
      {filterFields.map(({ key, label, type }) => {
        const value = filterValues[key as string];

        if (type === "select" || type === "multiselect") {
          return (
            <div key={String(key)} className="filter-field">
              <InputDropdown_New
                label={label}
                options={optionsMap[String(key)]}
                defaultValue={
                  type === "multiselect"
                    ? value || []
                    : value !== undefined && value !== null
                    ? { label: String(value), value }
                    : undefined
                }
                placeholder={`Selecciona ${label}`}
                searchable={true}
                multiple={type === "multiselect"}
                onChange={(selected) => {
                  if (type === "multiselect") {
                    // selected is (string | number)[]
                    return onChange(key as string, selected);
                  } else {
                    // selected is OptionType
                    return onChange(
                      key as string,
                      (selected as { value: any }).value
                    );
                  }
                }}
                required={false}
              />
            </div>
          );
        }

        if (type === "date") {
          return (
            <div key={String(key)} className="filter-field">
              <DateRangeCalendar
                label={label}
                initialStartDate={
                  value && value.start ? new Date(value.start) : null
                }
                initialEndDate={value && value.end ? new Date(value.end) : null}
                onAccept={({ start, end }: { start: Date; end: Date }) => {
                  onChange(key as string, { start, end });
                }}
                onClear={() => {
                  onChange(key as string, undefined);
                }}
              />
            </div>
          );
        }

        if (type === "cards") {
          return (
            <div key={String(key)} className="filter-field">
              <ul className="section-header__modal__section__content">
                {label}
                {optionsMap[String(key)].map((option) => (
                  <Tag
                    key={option.value}
                    label={option.label}
                    isSelected={
                      Array.isArray(value) && value.includes(option.value)
                    }
                    onSelect={() => {
                      const valueOpt = option.value;
                      const newVal = Array.isArray(value)
                        ? value.includes(valueOpt)
                          ? value.filter((v) => v !== valueOpt)
                          : [...value, valueOpt]
                        : [valueOpt];
                      onChange(key as string, newVal);
                    }}
                  />
                ))}
              </ul>
            </div>
          );
        }

        // Para otros tipos (text, date, number...), agrégalos según tu lógica
        return null;
      })}

      <div className="filters-actions">
        <button type="button" onClick={onClear}>
          Limpiar
        </button>
      </div>
    </div>
  );
}

export default FiltersDrawer;
